import { type FC, type ReactElement, useState } from 'react'
import { useNavigate } from 'react-router'
import { Radio } from '@/components/commons/radio'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	Dialog,
	DialogBody,
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { routes } from '@/routes'
import { type TKey, useTranslation } from '@/translations/i18n'
import { useRejectFinalReviewListing } from '../queries/use-reject-final-review-listing'
import type { FinalReviewRejectionReason } from '../types'

const NOTE_CHARACTER_LIMIT = 300
const REJECTION_REASONS: {
	value: FinalReviewRejectionReason
	label: TKey
}[] = [
	{
		value: 'does_not_meet_luxe_design_standards',
		label: 'luxe_submissions:rejection_reason_design_standards'
	},
	{
		value: 'does_not_meet_table_stake_requirements',
		label: 'luxe_submissions:rejection_reason_table_stake_requirements'
	},
	{
		value: 'does_not_have_key_luxe_amenities',
		label: 'luxe_submissions:rejection_reason_key_luxe_amenities'
	}
]

interface FinalReviewRejectionDialogProps {
	trigger: ReactElement
	listingId: string
}

export const FinalReviewRejectionDialog: FC<
	FinalReviewRejectionDialogProps
> = ({ trigger, listingId }) => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const [selectedReason, setSelectedReason] =
		useState<FinalReviewRejectionReason | null>(null)
	const [note, setNote] = useState('')
	const [open, setOpen] = useState(false)
	const rejectMutation = useRejectFinalReviewListing(listingId, {
		onSuccess: () => {
			setOpen(false)
			navigate(routes.LUXE_SUBMISSIONS_FINAL_REVIEW.handle.fullPath)
		}
	})

	const handleReject = () => {
		if (!selectedReason) {
			return
		}
		rejectMutation.mutate({
			reasons: [selectedReason],
			note: note || undefined
		})
	}

	return (
		<Dialog onOpenChange={setOpen} open={open}>
			<DialogTrigger render={trigger} />
			<DialogContent className='max-w-120' showCloseButton={false}>
				<DialogHeader>
					{t('luxe_submissions:listing_rejection')}
				</DialogHeader>
				<DialogBody>
					<DialogTitle>
						{t('luxe_submissions:rejection_question')}
					</DialogTitle>

					<div className='flex flex-col gap-2'>
						{REJECTION_REASONS.map((reason) => (
							<Radio
								checked={selectedReason === reason.value}
								key={reason.value}
								label={t(reason.label)}
								name='rejection-reason'
								onChange={() => setSelectedReason(reason.value)}
								value={reason.value}
								variant='brand'
							/>
						))}
					</div>

					<div className='flex flex-col gap-2'>
						<p className='text-neutral-900 text-sm'>
							{t('luxe_submissions:rejection_note_hint')}
						</p>
						<Label htmlFor='rejection-note'>
							{t('luxe_submissions:rejection_note_label')}
						</Label>
						<Textarea
							className='min-h-25'
							id='rejection-note'
							maxLength={NOTE_CHARACTER_LIMIT}
							onChange={(e) => setNote(e.target.value)}
							placeholder={t(
								'luxe_submissions:rejection_note_placeholder'
							)}
							rows={4}
							value={note}
						/>
						<p className='text-neutral-600 text-sm'>
							{note.length}/{NOTE_CHARACTER_LIMIT}
						</p>
					</div>
				</DialogBody>

				<DialogFooter className='flex flex-row justify-between bg-neutral-white'>
					<DialogClose
						render={
							<ButtonLegacy type='button' variant='tertiary' />
						}
					>
						{t('common:close')}
					</DialogClose>
					<ButtonLegacy
						disabled={!selectedReason || rejectMutation.isPending}
						onClick={handleReject}
						type='button'
						variant='brand'
					>
						{t('common:reject')}
					</ButtonLegacy>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
